import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchJson } from "../apiClient";
import "./AdminCollections.css";

const SCOPES = ["all", "mine", "shared", "system"];

const withDatabase = (path, database) =>
  database ? `${path}${path.includes("?") ? "&" : "?"}database=${encodeURIComponent(database)}` : path;

/** The agent library: cursor-paginated, scoped list of the agents the user can see, with
 * the actions the server says they may take (can_edit / can_run). */
export default function AgentLibrary({ database, onEdit, onStart }) {
  const { t } = useTranslation();
  const [scope, setScope] = useState("all");
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const generation = useRef(0);
  const moreInFlight = useRef(false);

  const load = useCallback(async (cursor) => {
    if (cursor) {
      if (moreInFlight.current) return;   // a double-click must not fetch (and append) the page twice
      moreInFlight.current = true;
      setLoadingMore(true);
    }
    const gen = cursor ? generation.current : ++generation.current;
    const path = `/api/v1/agents?scope=${scope}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`;
    try {
      const page = await apiFetchJson(path);
      if (gen !== generation.current) return;
      setItems((prev) => {
        if (!cursor) return page.items || [];
        const seen = new Set(prev.map((a) => a.id));
        return [...prev, ...(page.items || []).filter((a) => !seen.has(a.id))];
      });
      setNextCursor(page.next_cursor || null);
      setError("");
    } catch (e) {
      if (gen === generation.current) setError(e.message);
    } finally {
      if (cursor) {
        moreInFlight.current = false;
        setLoadingMore(false);
      }
    }
  }, [scope]);

  useEffect(() => { load(null); }, [load]);

  const act = async (request) => {
    setBusy(true);
    try {
      await request();
      await load(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = (agent) => act(async () => {
    await apiFetchJson(`/api/v1/agents/${agent.id}`, { method: "DELETE" });
    setConfirming(null);
  });

  const duplicate = (agent) => act(async () => {
    const copy = await apiFetchJson(withDatabase(`/api/v1/agents/${agent.id}/duplicate`, database), { method: "POST" });
    const dropped = (copy && copy.dropped_tools) || [];
    setNotice(dropped.length ? t("agents.copiedWithout", { tools: dropped.join(", ") }) : "");
  });

  return (
    <div className="ds-page">
      <header className="ds-page-header">
        <h2 className="ds-page-title">{t("agents.libraryTitle")}</h2>
        <p className="ds-page-subtitle">{t("agents.librarySubtitle")}</p>
      </header>
      {error && <div className="ds-alert ds-alert--danger" role="alert">{error}</div>}
      {notice && <div className="ds-alert ds-alert--info" role="status">{notice}</div>}
      <div className="ds-toolbar">
        {SCOPES.map((s) => (
          <button key={s} type="button" className="ds-chip" aria-pressed={scope === s} onClick={() => setScope(s)}>
            {t(`agents.scope${s[0].toUpperCase()}${s.slice(1)}`)}
          </button>
        ))}
        <button type="button" className="ds-btn ds-btn--primary ds-push-end" onClick={() => onEdit("new")}>
          {t("agents.newAgent")}
        </button>
      </div>
      {items.length === 0 ? <div className="ds-empty">{t("agents.empty")}</div> : (
        <ul className="ds-list">
          {items.map((agent) => (
            <li key={agent.id} className="ds-list-row">
              <div className="ac-item-row">
                <div className="ac-grow">
                  <span className="ds-list-title">{agent.name}</span>{" "}
                  <span className="ds-badge">
                    {agent.is_system ? t("agents.systemBadge")
                      : t(agent.visibility === "workspace" ? "agents.sharedBadge" : "agents.privateBadge")}
                  </span>
                  <p className="ds-help">{agent.description}</p>
                  <span className="ds-meta">
                    {t("agents.toolCount", { count: (agent.tools || []).length })}
                    {agent.owner_email && <> · {t("agents.owner", { email: agent.owner_email })}</>}
                  </span>
                </div>
                <div className="ac-actions">
                  {agent.can_run && (
                    <button type="button" className="ds-btn ds-btn--primary ds-btn--sm" onClick={() => onStart(agent)}>
                      {t("agents.start")}
                    </button>
                  )}
                  {agent.can_edit && (
                    <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" onClick={() => onEdit(agent)}>
                      {t("agents.edit")}
                    </button>
                  )}
                  <button type="button" className="ds-btn ds-btn--ghost ds-btn--sm" disabled={busy}
                          onClick={() => duplicate(agent)}>{t("agents.duplicate")}</button>
                  {agent.can_edit && (
                    <button type="button" className="ds-btn ds-btn--danger ds-btn--sm" disabled={busy}
                            onClick={() => setConfirming(agent)}>{t("agents.delete")}</button>
                  )}
                </div>
              </div>
              {confirming?.id === agent.id && (
                <div className="ac-confirm" role="alertdialog" aria-label={t("agents.delete")}>
                  <p>{t("agents.confirmDelete", { name: agent.name })}</p>
                  <button type="button" className="ds-btn ds-btn--danger ds-btn--sm" disabled={busy}
                          onClick={() => remove(agent)}>{t("agents.confirmDeleteButton")}</button>
                  <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm"
                          onClick={() => setConfirming(null)}>{t("agents.cancel")}</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {nextCursor && (
        <button type="button" className="ds-btn ds-btn--secondary ac-load-more" disabled={loadingMore} onClick={() => load(nextCursor)}>
          {t("agents.loadMore")}
        </button>
      )}
    </div>
  );
}
